/**
 * API client service for frontend
 * Handles all HTTP requests to backend API
 */

import { SUPPORTED_LANGUAGES } from '../constants/languages'
import type { LanguageCode } from '../../../shared/types'

// Prefer env; in dev default to Vite proxy '/api'. If proxy is misconfigured,
// auto-fallback to backend directly at http://localhost:3001/api.
let API_BASE_URL: string =
  (import.meta as any).env?.VITE_API_BASE_URL || ((import.meta as any).env?.DEV ? '/api' : 'http://localhost:3001/api')

let API_BASE_VERIFIED = false
async function ensureApiBaseHealthy(): Promise<void> {
  if (API_BASE_VERIFIED) return
  const candidates = Array.from(new Set<string>([
    API_BASE_URL,
    '/api',
    'http://localhost:3001/api',
  ]))

  for (const base of candidates) {
    try {
      // Probe health with a short timeout
      const ctl = new AbortController()
      const t = setTimeout(() => ctl.abort(), 1200)
      const res = await fetch(`${base}/health`, { method: 'GET', cache: 'no-store', signal: ctl.signal })
      clearTimeout(t)
      if (res.ok) {
        API_BASE_URL = base
        API_BASE_VERIFIED = true
        try { console.debug('[api] using base', API_BASE_URL) } catch {}
        return
      }
    } catch {}
  }
  // If none worked, keep existing; requests will surface error
  API_BASE_VERIFIED = true
}

export interface APIResponse<T> {
  success: boolean
  data?: T
  error?: {
    code: string
    message: string
    details?: Record<string, any>
  }
}

/**
 * Custom error class for API errors
 */
export class APIError extends Error {
  constructor(
    public statusCode: number,
    public code: string,
    message: string,
    public details?: Record<string, any>
  ) {
    super(message)
    this.name = 'APIError'
  }
}

/**
 * Get Firebase auth token for API requests
 */
async function getAuthToken(): Promise<string | null> {
  try {
    const { getAuth } = await import('firebase/auth')
    const auth = getAuth()
    const token = await auth.currentUser?.getIdToken()
    return token || null
  } catch (error) {
    console.error('Failed to get auth token:', error)
    return null
  }
}

/**
 * Make a fetch request with error handling
 */
async function fetchAPI<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  await ensureApiBaseHealthy()
  const url = `${API_BASE_URL}${endpoint}`

  const defaultHeaders: HeadersInit = {}

  // Add Firebase auth token if available (with timeout to prevent hanging)
  try {
    const tokenPromise = getAuthToken()
    const timeoutPromise = new Promise<null>((resolve) => setTimeout(() => resolve(null), 2000))
    const token = await Promise.race([tokenPromise, timeoutPromise])
    if (token) {
      ;(defaultHeaders as any)['Authorization'] = `Bearer ${token}`
    }
  } catch (err) {
    // Continue without auth token if it fails
    console.warn('[api] Failed to get auth token:', err)
  }

  const headers: HeadersInit = { ...defaultHeaders, ...(options.headers || {}) }
  // Only set JSON content-type when there is a body
  const hasBody = typeof (options as any).body !== 'undefined'
  if (hasBody && !('Content-Type' in (headers as any))) {
    ;(headers as any)['Content-Type'] = 'application/json'
  }

  // Force no caching with multiple headers
  const config: RequestInit = {
    ...options,
    cache: 'no-store',
    headers: {
      ...headers,
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0'
    }
  }

  try {
    try {
      // eslint-disable-next-line no-console
      console.debug('[api] fetch', { url, method: (options as any)?.method || 'GET' })
    } catch {}

    // Direct fetch without aggressive aborts (timeouts can cause false failures during dev)
    const response = await fetch(url, config)
    let data: APIResponse<T> | null = null
    let parseErr: any = null
    try {
      data = (await response.json()) as APIResponse<T>
    } catch (e) {
      parseErr = e
    }

    // Handle HTTP errors first (regardless of payload shape)
    if (!response.ok) {
      let message = data?.error?.message || response.statusText || 'An error occurred'
      if (!data && parseErr) {
        try {
          const text = await response.text()
          if (text) message = text
        } catch {}
      }
      throw new APIError(response.status, data?.error?.code || 'UNKNOWN_ERROR', message, data?.error?.details)
    }

    // Accept both wrapped { success, data } envelopes and plain JSON payloads
    const isEnvelope = data && typeof data === 'object' && 'success' in data
    if (isEnvelope) {
      const envelope = data as APIResponse<T>
      if (!envelope.success) {
        const message = envelope.error?.message || response.statusText || 'An error occurred'
        throw new APIError(response.status, envelope.error?.code || 'UNKNOWN_ERROR', message, envelope.error?.details)
      }
      return (envelope.data as T) ?? ({} as T)
    }

    return (data as T) ?? ({} as T)
  } catch (error) {
    if (error instanceof APIError) {
      throw error
    }

    // Network or parsing errors
    // Attempt one fallback retry direct to backend if current base is proxy
    if (API_BASE_URL === '/api') {
      try {
        API_BASE_URL = 'http://localhost:3001/api'
        API_BASE_VERIFIED = true
        // eslint-disable-next-line no-console
        console.warn('[api] retrying via direct backend', { endpoint })
        return fetchAPI<T>(endpoint, options)
      } catch {}
    }
    throw new APIError(0, 'NETWORK_ERROR', error instanceof Error ? error.message : 'Network error occurred')
  }
}

/**
 * GET request
 */
export async function get<T>(endpoint: string): Promise<T> {
  return fetchAPI<T>(endpoint, { method: 'GET' })
}

/**
 * POST request
 */
export async function post<T>(endpoint: string, data?: any): Promise<T> {
  return fetchAPI<T>(endpoint, {
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined,
  })
}

/**
 * PUT request
 */
export async function put<T>(endpoint: string, data?: any): Promise<T> {
  return fetchAPI<T>(endpoint, {
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined,
  })
}

/**
 * PATCH request
 */
export async function patch<T>(endpoint: string, data?: any): Promise<T> {
  return fetchAPI<T>(endpoint, {
    method: 'PATCH',
    body: data ? JSON.stringify(data) : undefined,
  })
}

/**
 * DELETE request
 */
export async function del<T>(endpoint: string): Promise<T> {
  return fetchAPI<T>(endpoint, { method: 'DELETE' })
}

/**
 * Upload file with multipart/form-data
 */
export async function uploadFile<T>(
  endpoint: string,
  file: File,
  fieldName: string = 'file',
  additionalData?: Record<string, string>
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`

  const formData = new FormData()
  formData.append(fieldName, file)

  if (additionalData) {
    Object.entries(additionalData).forEach(([key, value]) => {
      formData.append(key, value)
    })
  }

  const response = await fetch(url, {
    method: 'POST',
    body: formData,
    // Don't set Content-Type header - browser will set it with boundary
  })
  let data: APIResponse<T> | null = null
  try {
    data = (await response.json()) as APIResponse<T>
  } catch {}
  if (!response.ok || !data?.success) {
    let message = data?.error?.message || response.statusText || 'File upload failed'
    if (!data) {
      try {
        const text = await response.text()
        if (text) message = text
      } catch {}
    }
    throw new APIError(response.status, data?.error?.code || 'UPLOAD_ERROR', message, data?.error?.details)
  }
  return (data?.data as T) ?? ({} as T)
}

/**
 * Health check
 */
export async function healthCheck(): Promise<{
  status: string
  timestamp: string
  environment: string
  uptime: number
}> {
  return get('/health')
}

/**
 * Conversation API methods
 */

export interface LogGuestStartRequest {
  languageCode: LanguageCode
  landingPagePath?: string
  referrer?: string
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
}

export interface LogGuestStartResponse {
  guestId: string
  guestUserId: string
  createdAt: string
}

export async function logGuestStart(request: LogGuestStartRequest): Promise<LogGuestStartResponse> {
  return post('/guest/start', request)
}

export interface StartConversationRequest {
  userId: string
  difficultyLevel: 'beginner' | 'intermediate' | 'advanced'
  requestedTopic?: string
  aiPersonality?: string
  targetLanguage?: string
}

export interface StartConversationResponse {
  session: {
    id: number
    userId: number
    difficultyLevel: string
    status: string
    startTime: string
  }
  aiGreeting: {
    text: string
    audioUrl?: string
  }
}

export async function startConversation(
  request: StartConversationRequest
): Promise<StartConversationResponse> {
  return post('/conversation/start', request)
}

export interface SendExchangeResponse {
  userExchange: {
    id: number
    text: string
    timestamp: string
    pronunciationFeedback?: string
    learningNotes?: string | null
    confidenceScore: number
  }
  aiExchange: {
    id: number
    text: string
    audioUrl?: string
    timestamp: string
  }
}

export async function sendExchange(
  sessionId: number,
  audioBlob?: Blob,
  text?: string,
  aiPersonality?: string
): Promise<SendExchangeResponse> {
  const personalityPayload = aiPersonality ? { aiPersonality } : undefined
  if (audioBlob) {
    // Send as multipart/form-data with audio file
    return uploadFile(
      `/conversation/${sessionId}/exchange`,
      new File([audioBlob], 'audio.webm'),
      'audio',
      personalityPayload
    )
  } else if (text) {
    // Send as JSON with text only
    return post(`/conversation/${sessionId}/exchange`, {
      userText: text,
      ...personalityPayload,
    })
  }

  throw new Error('Either audio or text is required')
}

export interface EndConversationResponse {
  session: {
    id: number
    status: string
    durationSeconds: number
    xpEarned: number
  }
  xpEarned: number
  newLevel: number
  leveledUp: boolean
  grammarNotes?: string[]
  pronunciationNotes?: string[]
  streakData?: {
    newStreak: number
    streakMaintained: boolean
    streakBroken: boolean
    dailyGoalCompleted: boolean
    previousStreak?: number // The streak count before it was broken
  }
}

export async function endConversation(
  sessionId: number,
  status: 'completed' | 'interrupted' = 'completed',
  localDate?: string
): Promise<EndConversationResponse> {
  const payload: Record<string, any> = { status }
  if (localDate) {
    payload.localDate = localDate
  }
  return post(`/conversation/${sessionId}/end`, payload)
}

export async function getConversationHistory(
  userId: string,
  limit = 20,
  offset = 0
): Promise<{
  sessions: any[]
  total: number
  limit: number
  offset: number
}> {
  return get(`/conversation/history?userId=${userId}&limit=${limit}&offset=${offset}`)
}

export async function getSessionExchanges(sessionId: number): Promise<{ exchanges: any[] }> {
  return get(`/conversation/${sessionId}/exchanges`)
}

/**
 * Translation API methods
 */
export async function translateWordAPI(input: {
  word: string
  userId: number | string
  sessionId: string
  context?: string
  targetLanguage?: string
  skipFlashcardCreate?: boolean
  audioUrl?: string
}): Promise<{
  word: string
  translation: string
  furigana: string
  exampleSentence: string
  exampleTranslation: string
  flashcardCreated?: boolean
}> {
  return post('/translate/word', input)
}

const DEFAULT_TTS_LOCALE = 'ja-JP'

function resolveTtsLocale(language?: string): string {
  if (!language) return DEFAULT_TTS_LOCALE
  const trimmed = language.trim()
  if (!trimmed) return DEFAULT_TTS_LOCALE

  // If the caller already provided a locale (e.g., zh-CN), honor it
  if (trimmed.includes('-')) {
    return trimmed
  }

  const matchedLanguage = SUPPORTED_LANGUAGES.find((lang) => lang.code === trimmed)
  if (matchedLanguage?.ttsLanguageCode) {
    return matchedLanguage.ttsLanguageCode
  }

  if (trimmed === 'en') return 'en-US'
  return DEFAULT_TTS_LOCALE
}

export async function speakJapaneseText(
  text: string,
  provider: 'google' | 'elevenlabs' = 'google',
  voiceId?: string | null,
  language?: string
): Promise<{
  text: string
  audioUrl: string
  provider: string
}> {
  const trimmedVoiceId = typeof voiceId === 'string' ? voiceId.trim() : ''
  const payload: Record<string, unknown> = { text, provider }
  if (trimmedVoiceId) {
    payload.voiceId = trimmedVoiceId
  }
  payload.language = resolveTtsLocale(language)
  return post('/translate/speak', payload)
}

export async function translateSentenceAPI(input: {
  sentence: string
  userId?: number | string
  targetLanguage?: string
}): Promise<{
  sentence: string
  translation: string
}> {
  return post('/translate/sentence', input)
}

export async function convertToRomajiAPI(
  text: string,
  languageCode?: string
): Promise<{
  text: string
  romaji: string
}> {
  const payload: Record<string, string> = { text }
  if (languageCode) {
    payload.languageCode = languageCode
  }
  return post('/translate/romaji', payload)
}

export interface ElevenLabsVoice {
  voiceId: string
  name: string
  previewUrl?: string
  labels?: Record<string, string>
  description?: string
  supportsJapanese?: boolean
}

export async function getElevenLabsVoices(): Promise<{
  voices: ElevenLabsVoice[]
  message?: string
}> {
  return get('/translate/voices')
}

/**
 * User API methods
 */

export interface CreateUserRequest {
  username: string
  email?: string
  difficultyLevel?: 'beginner' | 'intermediate' | 'advanced'
  showFurigana?: boolean
  dailyReviewTarget?: number
  pronunciationStrictness?: 'relaxed' | 'moderate' | 'strict'
  subscriptionTier?: 'free' | 'standard' | 'premium'
  preferredVoiceId?: string
  themePreference?: 'light' | 'dark'
}

export async function createUser(request: CreateUserRequest): Promise<{ user: any }> {
  return post('/user/create', request)
}

export async function loginUser(email: string, password: string): Promise<{ user: any }> {
  return post('/user/login', { email, password })
}

export async function getUserProfile(userId: string): Promise<{
  user: any
  progression: {
    xpForNextLevel: number
    xpProgress: number
    xpTotal: number
  }
}> {
  return get(`/user/profile/${userId}`)
}

export async function updateUserProfile(
  userId: string,
  updates: Partial<CreateUserRequest>
): Promise<{ user: any }> {
  return put(`/user/profile/${userId}`, updates)
}

/**
 * Flashcards API methods
 */
export async function generateFlashcards(
  sessionId: number,
  userId: number
): Promise<{ flashcards: any[]; count: number; skippedDuplicates: number }> {
  return post('/flashcards/generate', { sessionId, userId })
}

export async function getFlashcards(
  userId: string,
  options?: { limit?: number; offset?: number; masteryLevel?: 'new' | 'learning' | 'review' | 'mastered'; targetLanguage?: string }
): Promise<{ flashcards: any[]; totalCount: number; masteryBreakdown: Record<string, number>; limit: number; offset: number }> {
  const params = new URLSearchParams({ userId, limit: String(options?.limit ?? 100), offset: String(options?.offset ?? 0), ts: String(Date.now()) })
  if (options?.masteryLevel) params.append('masteryLevel', options.masteryLevel)
  if (options?.targetLanguage) params.append('targetLanguage', options.targetLanguage)
  return get(`/flashcards?${params.toString()}`)
}

export async function deleteFlashcard(cardId: number): Promise<{ success: boolean }> {
  return del(`/flashcards/${cardId}`)
}

export async function deleteFlashcardByDoc(docId: string): Promise<{ success: boolean }> {
  return del(`/flashcards/doc/${encodeURIComponent(docId)}`)
}

export async function createFlashcard(input: {
  userId: number | string
  japaneseText: string
  furigana: string
  englishTranslation: string
  exampleSentence: string
  exampleTranslation: string
}): Promise<{ flashcard: any }> {
  return post('/flashcards', input)
}

export async function getReviewQueueAPI(userId: string, limit = 50, targetLanguage?: string): Promise<{ flashcards: any[]; count: number }> {
  const params = new URLSearchParams({ userId, limit: String(limit) })
  if (targetLanguage) params.append('targetLanguage', targetLanguage)
  return get(`/flashcards/review-queue?${params.toString()}`)
}

export async function getReviewProgressAPI(userId: string, dailyTarget: number): Promise<{ reviewedToday: number; totalDue: number; percentageComplete: number; remaining: number }> {
  const params = new URLSearchParams({ userId, dailyTarget: String(dailyTarget) })
  return get(`/flashcards/review/progress?${params.toString()}`)
}

export async function submitFlashcardReviewAPI(
  cardId: number,
  input: {
    userId: string | number
    rating: 'again' | 'hard' | 'good' | 'easy'
    timeToAnswerMs: number
    isAudioMode?: boolean
    localDate?: string
  }
): Promise<{ flashcard: any }> {
  return post(`/flashcards/${cardId}/review`, { ...input })
}

export async function submitFlashcardReviewByDocIdAPI(
  docId: string,
  input: {
    userId: string | number
    rating: 'again' | 'hard' | 'good' | 'easy'
    timeToAnswerMs: number
    isAudioMode?: boolean
    localDate?: string
  }
): Promise<{ flashcard: any }> {
  return post(`/flashcards/doc/${encodeURIComponent(docId)}/review`, { ...input })
}

// Add review time to user
export async function addReviewTimeToUser(userId: string, seconds: number): Promise<{ user: any }> {
  return post(`/user/${userId}/review-time`, { seconds })
}

/**
 * Subscription API methods
 */
export interface SubscriptionStatus {
  tier: 'free' | 'standard' | 'premium'
  status?: string
  currentPeriodEnd?: number
  cancelAtPeriodEnd?: boolean
}

export async function createCheckoutSession(
  successUrl: string,
  cancelUrl: string,
  tier: 'standard' | 'premium' = 'premium',
  interval: 'monthly' | 'yearly' = 'monthly'
): Promise<{ sessionId: string; url: string }> {
  const response = await post<{ sessionId: string; url: string }>('/subscription/create-checkout-session', { successUrl, cancelUrl, tier, interval })
  return response
}

export async function createPortalSession(
  returnUrl: string
): Promise<{ url: string }> {
  const response = await post<{ url: string }>('/subscription/create-portal-session', { returnUrl })
  return response
}

export async function getSubscriptionStatus(userId: string): Promise<SubscriptionStatus> {
  const response = await get<SubscriptionStatus>(`/subscription/status/${userId}`)
  return response
}

/**
 * Study Plan API methods (Premium feature)
 */
export interface StudyPlan {
  id: string
  userId: string
  languageCode: string
  goalHours: number
  targetDate: string
  startDate: string
  studyDays: number[]
  dailyStudyMinutes: number
  status: 'active' | 'completed' | 'failed' | 'cancelled'
  currentProgress: number
  createdAt: string
  updatedAt: string
}

export interface StudyPlanStats {
  totalStudyDays: number
  completedStudyDays: number
  daysRemaining: number
  progressPercentage: number
  isOnTrack: boolean
  hoursCompleted: number
  hoursRemaining: number
  expectedProgressPercentage: number
}

export interface StudyPlanDisplay {
  plan: StudyPlan
  stats: StudyPlanStats
  recommendations: {
    suggestedDailyMinutes: number
    isAchievable: boolean
    message: string
  }
}

export interface DailyStudyLog {
  id?: string
  date: string
  studyTimeSeconds: number
  conversationTimeSeconds?: number
  flashcardStudySeconds?: number
  conversationSessionsCount: number
  flashcardsReviewedCount: number
  xpGained: number
  targetLanguage?: string
  languageBreakdown?: Record<
    string,
    {
      studyTimeSeconds: number
      conversationTimeSeconds: number
      flashcardStudySeconds: number
    }
  >
}

export interface CreateStudyPlanRequest {
  languageCode: string
  goalHours: number
  targetDate?: string // Optional for reverse mode
  dailyStudyMinutes?: number // Optional for forward mode
  studyDays: number[]
}

export interface UpdateStudyPlanRequest {
  languageCode?: string
  goalHours?: number
  targetDate?: string
  studyDays?: number[]
  status?: 'active' | 'completed' | 'failed' | 'cancelled'
}

export async function createStudyPlan(request: CreateStudyPlanRequest): Promise<StudyPlan> {
  return post('/study-plan', request)
}

export async function getActiveStudyPlan(): Promise<{ plan: StudyPlan; stats: StudyPlanStats } | null> {
  return get('/study-plan/active')
}

export async function getAllStudyPlans(): Promise<StudyPlan[]> {
  return get('/study-plan/all')
}

export async function getStudyPlan(planId: string): Promise<StudyPlanDisplay> {
  return get(`/study-plan/${planId}`)
}

export async function updateStudyPlan(planId: string, updates: UpdateStudyPlanRequest): Promise<StudyPlan> {
  return patch(`/study-plan/${planId}`, updates)
}

export async function cancelStudyPlan(planId: string): Promise<StudyPlan> {
  return post(`/study-plan/${planId}/cancel`)
}

export async function deleteStudyPlan(planId: string): Promise<{ message: string }> {
  return del(`/study-plan/${planId}`)
}

export async function getDailyStudyLogs(
  userId: string,
  startDate: string,
  endDate: string
): Promise<DailyStudyLog[]> {
  const params = new URLSearchParams({
    userId,
    startDate,
    endDate,
  })
  const response = await get<{ logs: DailyStudyLog[] }>(`/progress/daily?${params.toString()}`)
  return response.logs
}

export async function getTodaysSummary(userId: string): Promise<{
  studyTimeSeconds: number
  conversationSessions: number
  flashcardsReviewed: number
  xpGained: number
  totalAIResponses: number
}> {
  const params = new URLSearchParams({ userId })
  return get(`/progress/today?${params.toString()}`)
}

// ============================================================================
// Feedback API
// ============================================================================

export interface SubmitFeedbackRequest {
  feedbackType: 'review' | 'feedback' | 'bug' | 'feature'
  message: string
  rating?: number
}

export async function submitFeedback(request: SubmitFeedbackRequest): Promise<{
  message: string
  emailSent: boolean
}> {
  return post('/feedback', request)
}

// Export default client
export default {
  get,
  post,
  put,
  patch,
  del,
  uploadFile,
  healthCheck,
  logGuestStart,
  startConversation,
  sendExchange,
  endConversation,
  getConversationHistory,
  getSessionExchanges,
  createUser,
  getUserProfile,
  updateUserProfile,
  loginUser,
  translateWordAPI,
  speakJapaneseText,
  convertToRomajiAPI,
  generateFlashcards,
  getFlashcards,
  deleteFlashcard,
  deleteFlashcardByDoc,
  createFlashcard,
  getReviewQueueAPI,
  getReviewProgressAPI,
  submitFlashcardReviewAPI,
  submitFlashcardReviewByDocIdAPI,
  addReviewTimeToUser,
  createCheckoutSession,
  createPortalSession,
  getSubscriptionStatus,
  createStudyPlan,
  getActiveStudyPlan,
  getAllStudyPlans,
  getStudyPlan,
  updateStudyPlan,
  cancelStudyPlan,
  deleteStudyPlan,
  getDailyStudyLogs,
  getTodaysSummary,
  submitFeedback,
  APIError,
}
